import { SemanticCOLORS } from 'semantic-ui-react';
import { PlagiarismServiceProviders } from './PlagiarismServiceProviders';
import { RootState } from '../store/rootReducer';
import storeManager from '../store';

export const OriginalityValues = {
    LOW: 'Low risk',
    MEDIUM: 'Medium risk',
    HIGH: 'High risk',
} as const;

export type OriginalityValue = typeof OriginalityValues[keyof typeof OriginalityValues];

export interface OriginalityDisplayData {
    score?: number | null;
    originalityLabel?: OriginalityValue | null;
    plagiarismServiceProvider?: PlagiarismServiceProviders;
}

const originalityToColor: Record<OriginalityValue, SemanticCOLORS> = {
    [OriginalityValues.LOW]: 'green',
    [OriginalityValues.MEDIUM]: 'yellow',
    [OriginalityValues.HIGH]: 'red',
};

export interface PlagiarismReport {
    id: string;
    marketplaceId: string;
    plagiarismServiceProvider: PlagiarismServiceProviders | undefined;
    state: string;
    reportUrl: string;
    significance: number | OriginalityValue;
    externalStatusMessage?: any;
    content: { [index: string]: any };
}

export const plagiarismReportState = {
    FINISHED_HIDDEN: 'FINISHED_HIDDEN',
    FINISHED: 'FINISHED',
    DUPLICATE_HIDDEN: 'DUPLICATE_HIDDEN',
    DUPLICATE: 'DUPLICATE',
    SUBMITTED: 'SUBMITTED',
    NOT_SUBMITTED: 'NOT_SUBMITTED',
    NOT_ENOUGH_TEXT: 'NOT_ENOUGH_TEXT',
    NO_TEXT: 'NO_TEXT',
    CORRUPT: 'CORRUPT',
    PROTECTED: 'PROTECTED',
    REJECTED: 'REJECTED',
    EXTERNAL_ERROR: 'EXTERNAL_ERROR',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    S3_EXTERNAL_ERROR: 'S3_EXTERNAL_ERROR',
    SUBMISSION_TOO_SHORT: 'SUBMISSION_TOO_SHORT',
    PROCESSING: 'PROCESSING',
    PROCESSING_UPLOAD: 'PROCESSING_UPLOAD',
    REPORT_REQUESTED: 'REPORT_REQUESTED',
    NO_PLANNER_WITH_EULA: 'NO_PLANNER_WITH_EULA',
    NEED_TRIGGERING: 'NEED_TRIGGERING',
};

export const supportedQuestionTypes = {
    CONTENT_QUESTION_QTI2_UPLOAD: 'content_question_qti2_upload',
    CONTENT_QUESTION_QTI2_EXTENDEDTEXT: 'content_question_qti2_extendedtext',
};

const supportedMimeTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.sun.xml.writer',
    'application/vnd.ms-powerpoint',
    'application/vnd.ms-works',
    'application/vnd.ms-excel',
    'application/pdf',
    'application/rtf',
    'application/octet-stream',
    'application/postscript',
    'text/plain',
    'text/html',
    'text/richtext',
];

export function parsePlagiarismResponse(plagiarismServiceResponse: any) {
    let serviceProvider: PlagiarismServiceProviders | undefined;
    let res: any;
    let plagiarismReport: PlagiarismReport = null;

    if (!plagiarismServiceResponse || !plagiarismServiceResponse.answer) {
        return undefined;
    }

    const { answer } = plagiarismServiceResponse;
    for (const key in answer) {
        if (key in answer) {
            if (key.split('-').length === 2
                && parseInt(key.split('-')[0], 10) > 0
                && parseInt(key.split('-')[1], 10) > 0) {
                res = JSON.parse(answer[key]);
            }

            if (key === 'AntiPlagiarismServiceImpl') {
                serviceProvider = getPlagiarismServiceProviderName(answer[key]);
            }
        }
    }

    if (serviceProvider) {
        plagiarismReport = {
            ...plagiarismReport,
            plagiarismServiceProvider: serviceProvider,
        };

        if (res) {
            const {
                id, marketplaceId, content, state,
            } = res;
            let reportUrl = '';
            let significance = null;

            if (content) {
                if (serviceProvider === PlagiarismServiceProviders.TURNITIN) {
                    reportUrl = content.viewerUrl;
                    significance = content.overall_match_percentage;
                } else if (serviceProvider === PlagiarismServiceProviders.CROSSPLAG && res.documentId) {
                    reportUrl = content.reportUrl;
                    significance = typeof content.score === 'number' ? content.score : content.originality;
                } else if (content.Report) {
                    reportUrl = content.Report.ReportUrl;
                    significance = content.Report.Significance;
                }
            }

            plagiarismReport = {
                ...plagiarismReport,
                ...res,
                id,
                marketplaceId,
                state,
                reportUrl,
                significance,
            };
        }
    }

    return plagiarismReport;
}

const getPlagiarismServiceProviderName = (javaClassName: string): PlagiarismServiceProviders | undefined => {
    const loweredJavaClassName = javaClassName.toLowerCase();
    if (loweredJavaClassName.indexOf('urkund') !== -1) { return PlagiarismServiceProviders.URKUND; }
    if (loweredJavaClassName.indexOf('turnitin') !== -1) { return PlagiarismServiceProviders.TURNITIN; }
    if (loweredJavaClassName.indexOf('crossplag') !== -1) { return PlagiarismServiceProviders.CROSSPLAG; }
    if (loweredJavaClassName.indexOf('ephorus') !== -1) { return PlagiarismServiceProviders.EPHORUS; }
    return undefined;
};

export const isFinished = (state: string): boolean => {
    return state === plagiarismReportState.FINISHED
        || state === plagiarismReportState.FINISHED_HIDDEN;
};

export function isSupportedQuestionType(questionType: string): boolean {
    return questionType.toUpperCase() in supportedQuestionTypes;
}

export function isMimeTypeRestricted(questionType: string): boolean {
    return questionType === supportedQuestionTypes.CONTENT_QUESTION_QTI2_UPLOAD;
}

export function hasSupportedMimeType(mimeType: string): boolean {
    return supportedMimeTypes.includes(mimeType);
}

export function getColorByPlagiarismScore(score: number | OriginalityValue): SemanticCOLORS {
    return (typeof score === 'number') ? getColorByPlagiarismSignificance(score) : getColorByPlagiarismOriginality(score);
}

export function getColorByPlagiarismSignificance(significance: number): SemanticCOLORS {
    const { assessment } = (storeManager.getState() as RootState);
    const isGradingPlagiarismColorDisabled = assessment?.gradingPlagiarismColorDisabled;

    if (isGradingPlagiarismColorDisabled) {
        return 'grayLabel' as SemanticCOLORS;
    }

    if (significance === 0) {
        return 'blue';
    }

    if (significance > 0 && significance <= 24) {
        return 'green';
    }

    if (significance > 24 && significance <= 49) {
        return 'yellow';
    }

    if (significance > 49 && significance <= 74) {
        return 'orange';
    }

    if (significance > 74) {
        return 'red';
    }

    return null;
}

function getColorByPlagiarismOriginality(originality: OriginalityValue): SemanticCOLORS {
    const { assessment } = (storeManager.getState() as RootState);
    const isGradingPlagiarismColorDisabled = assessment?.gradingPlagiarismColorDisabled;

    return isGradingPlagiarismColorDisabled ? 'grayLabel' as SemanticCOLORS : originalityToColor[originality] || null;
}

export function getAvailablePlagiarismProviders() {
    return Object.values(PlagiarismServiceProviders).filter((item) => {
        return isNaN(Number(item));
    });
}

export function getOriginalityValueByScore(score: number): OriginalityValue {
    if (score > 66) {
        return OriginalityValues.HIGH;
    }

    if (score > 33) {
        return OriginalityValues.MEDIUM;
    }

    return OriginalityValues.LOW;
}

export function getOriginalityDisplayText(score: number | OriginalityValue | OriginalityDisplayData): string {
    const { assessment } = (storeManager.getState() as RootState);

    if (typeof score === 'object' && score !== null) {
        const serviceProvider = score.plagiarismServiceProvider || assessment?.antiPlagiarismServiceProvider;
        const shouldDisplayPercentage = serviceProvider === PlagiarismServiceProviders.CROSSPLAG
            && assessment?.displayIOPercentageScore;

        if (shouldDisplayPercentage && typeof score.score === 'number') {
            return `${score.score}%`;
        }

        if (score.originalityLabel) {
            return score.originalityLabel;
        }

        if (typeof score.score === 'number') {
            return getOriginalityValueByScore(score.score);
        }
    }

    if (typeof score === 'number') {
        if (assessment?.antiPlagiarismServiceProvider === PlagiarismServiceProviders.CROSSPLAG
            && assessment?.displayIOPercentageScore) {
            return `${score}%`;
        }

        return getOriginalityValueByScore(score);
    }

    if (typeof score === 'string') {
        return score;
    }
    return '';
}

export enum PlagiarismScoreTypes {
    Similarity,
    Originality,
}

export function getPlagiarismScoreType(serviceProvider: string | undefined) {
    if (serviceProvider?.toLowerCase() === PlagiarismServiceProviders.CROSSPLAG) {
        return PlagiarismScoreTypes.Originality;
    }

    return PlagiarismScoreTypes.Similarity;
}

export function toKnownPlagiarismServiceProvider(provider: string): PlagiarismServiceProviders | undefined {
    const knownProvider = Object.values(PlagiarismServiceProviders).find((p) => p === provider.toLowerCase());
    if (!knownProvider) {
        console.warn(`Unknown plagiarism data provider: ${provider}`);
    }

    return knownProvider;
}
